import React from "react";
import { ApiError, cockpit, type FormContactView } from "@/api";
import { Banner, Button, Input } from "@/ds";
import { useT } from "../../i18n";

export function FormCustomerConfirmation({ requestId, contact, onChanged }: {
  requestId: string; contact: FormContactView; onChanged: () => Promise<void>;
}) {
  const t = useT();
  const [email, setEmail] = React.useState(contact.suggested_email ?? "");
  const [acknowledged, setAcknowledged] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const inputId = React.useId();
  React.useEffect(() => {
    setEmail(contact.suggested_email ?? "");
    setAcknowledged(false);
  }, [requestId, contact.source_hash, contact.expected_version, contact.expected_revision,
    contact.expected_inbound_count, contact.suggested_email]);
  const confirm = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || !acknowledged || !email.trim() || !contact.can_confirm) return;
    setBusy(true);
    setError(null);
    try {
      await cockpit.confirmFormCustomer(requestId, {
        email, source_message_id: contact.source_message_id, source_hash: contact.source_hash,
        expected_version: contact.expected_version, expected_revision: contact.expected_revision,
        expected_inbound_count: contact.expected_inbound_count,
      });
      await onChanged();
    } catch (failure) {
      setAcknowledged(false);
      setError(t(failure instanceof ApiError && failure.status === 409 ? "ws.form.stale"
        : failure instanceof ApiError && failure.status === 400 ? "ws.form.invalid" : "ws.form.error"));
      if (failure instanceof ApiError && failure.status === 409) await onChanged();
    } finally { setBusy(false); }
  };
  if (contact.state === "confirmed") return null;
  return <form className="form-customer-confirmation" onSubmit={confirm}>
    {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}
    <label htmlFor={inputId}>{t("ws.form.email")}</label>
    <Input id={inputId} type="email" autoComplete="off" value={email} required maxLength={254}
      disabled={busy || !contact.can_confirm}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
        setAcknowledged(false);
      }} />
    <p className="muted">{t(contact.suggested_email ? "ws.form.replyToHint" : "ws.form.emailHint")}</p>
    <label className="form-customer-confirmation__check">
      <input type="checkbox" checked={acknowledged} disabled={busy || !contact.can_confirm}
        onChange={(event) => setAcknowledged(event.target.checked)} />
      <span>{t("ws.form.acknowledgement")}</span>
    </label>
    <Button type="submit" variant="primary" disabled={busy || !contact.can_confirm || !acknowledged || !email.trim()}>
      {t(busy ? "ws.form.confirming" : "ws.form.confirm")}
    </Button>
    {!contact.can_confirm && <p className="muted">{t("ws.form.readOnly")}</p>}
  </form>;
}
